import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchAdminStorefrontBundlesQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';
import { IStorefrontBundleView } from '../../models/StorefrontProducts';
import GridComposer from '../GridComposer/GridComposer';
import { GridSelectionMode, IColumnDetails } from '../../models/GridConfiguration';
import { PrimaryButton } from '@fluentui/react';
import StorefrontBundleModal from '../ProductBundles/StorefrontBundleModal';
import SuggestedBundlesStrip from './SuggestedBundlesStrip';
import { setOpenBundleId } from '../../store/settingsSlice';

interface StorefrontBundlesAdminTableProps {
    storefront: string;
}

const StorefrontBundlesAdminTable: React.FC<StorefrontBundlesAdminTableProps> = ({ storefront }) => {
    const dispatch = useDispatch();
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [filter, setFilter] = useState<string>();
    const [filterField, setFilterField] = useState<string>();
    const [sortBy, setSortBy] = useState<string>();
    const [sortDirection, setSortDirection] = useState<string>();

    const conditions = { page, pageSize, searchTerm: filter, searchBy: filterField, sortBy, sortDirection };
    const bundlesQuery = useSearchAdminStorefrontBundlesQuery(
        { storefrontId: storefront, conditions },
        { skip: !storefront }
    );

    const getCustomColumns = (): IColumnDetails[] => [
        {
            index: 0,
            identifier: 'details',
            label: '',
            type: '',
            component: (bundle: IStorefrontBundleView) => (
                // Bundles have no route of their own — the app-level BundleModalHost (mounted once
                // beside the ToastContainer) is the single place that opens the editor, addressable
                // both via ?panel=bundle&instance=<id> and via this redux intent. Mounting a second,
                // locally-owned <StorefrontBundleModal> here (as this used to) bypassed that wiring:
                // a /go/setting/bundles.questions... link could never reach a bundle opened this way,
                // and nothing stopped BOTH modals being open at once if one was ever opened via URL.
                <PrimaryButton onClick={() => dispatch(setOpenBundleId(bundle.id))}>Details</PrimaryButton>
            ),
            required: true,
        },
    ];

    return (
        <div style={{ overflowX: 'auto' }}>
            {/* Renders nothing unless there are bundles worth putting on this store, so the tab is unchanged
                for everyone else. The grid carries no cache tags, so a bundle listed from the strip only
                shows up here because of this refetch. */}
            <SuggestedBundlesStrip storefront={storefront} onBundlesAdded={() => bundlesQuery.refetch()} />

            <GridComposer
                data={bundlesQuery.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={bundlesQuery.isLoading || bundlesQuery.isFetching}
                onRefresh={() => bundlesQuery.refetch()}
                selectionMode={GridSelectionMode.none}
                columnSetName={'StorefrontBundle'}
                filter={filter}
                filterField={filterField}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => {
                    setSortBy(column);
                    setSortDirection(direction);
                }}
                onFilter={(f, column) => {
                    setFilter(f);
                    setFilterField(column);
                }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={true}
                getItemId={(item: IStorefrontBundleView) => item.id}
            />
        </div>
    );
};

export default StorefrontBundlesAdminTable;
